import { memo, useEffect, useMemo, useState } from 'react'

import { copy } from '~/modules/lunya'
import {
  BottomSheet,
  HStack,
  Icon,
  LoadingState,
  Placeholder,
  Pressable,
  SearchInput,
  Text,
  VStack,
} from '~/shared/ui'

import { SearchCitiesQuery } from '../../api'
import type { City } from '../../types'
import {
  CITY_EMPTY_DESCRIPTION,
  CITY_EMPTY_TITLE,
  CITY_POPULAR_OVERLINE,
  CITY_SEARCH_PLACEHOLDER,
  CITY_SELECTED_ICON_SIZE,
  CITY_SELECTED_ICON_STROKE,
} from './config'
import type { CityPickerSheetProps, SelectedCity } from './types'

import styles from './CityPickerSheet.module.scss'

/**
 * Элемент списка. `city === null` — это подставленный сверху выбранный город, которого нет
 * в ответе ручки: показать его мы можем, а отдать наружу полноценный `City` (с координатами
 * и таймзоной) — нет, поэтому такая строка ничего не меняет, только закрывает шторку.
 */
type CityRowItem = {
  id: string
  name: string
  region: string | null
  city: City | null
}

/** Одна строка города — общий вид для «Популярных», выбранного города и результатов поиска. */
const CityRow = memo(function CityRow(props: {
  item: CityRowItem
  isSelected: boolean
  onPress: (item: CityRowItem) => void
}) {
  const { item, isSelected, onPress } = props

  return (
    <Pressable
      as='div'
      role='button'
      tabIndex={0}
      onPress={() => onPress(item)}
      ariaLabel={item.name}
    >
      <HStack justify='BETWEEN' align='CENTER' gap='SMALL_M'>
        <div className={styles.row}>
          <Text variant='BODY' color={isSelected ? 'ACCENT' : 'PRIMARY'}>
            {item.name}
          </Text>
          {item.region && (
            <Text variant='CAPTION' color='MUTED'>
              {item.region}
            </Text>
          )}
        </div>
        {isSelected && (
          <div className={styles.check}>
            <Icon
              name='check'
              size={CITY_SELECTED_ICON_SIZE}
              strokeWidth={CITY_SELECTED_ICON_STROKE}
            />
          </div>
        )}
      </HStack>
    </Pressable>
  )
})

/**
 * Шторка «Город рождения» (спека 02 §A2·2). Единственный шаг слайса с сетевым запросом здесь.
 *
 * До ввода показывает самые населённые города России (пустой `q`), при вводе — поиск
 * по подстроке. Шторка заметно выше стандартной (см. .module.scss) — список скроллится,
 * поиск при этом закреплён в шапке и не уезжает вместе со списком.
 *
 * Уже выбранный город (`selectedCity`) отмечен галочкой, а если его нет среди популярных —
 * стоит отдельной строкой над ними: человек должен видеть, что у него выбрано, не листая.
 */
export const CityPickerSheet = memo(function CityPickerSheet(props: CityPickerSheetProps) {
  const { isOpen, onClose, onSelect, selectedCity = null } = props
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (isOpen) {
      setQuery('')
    }
  }, [isOpen])

  const trimmedQuery = query.trim()
  const isSearching = trimmedQuery.length > 0

  // Пустой q — это запрос популярных городов, ручка отдаёт их по полю rank
  // (apps/api/src/modules/city/city.repository.ts), поэтому запрос один на оба режима.
  const { data: cities = [], isLoading } = SearchCitiesQuery.useQuery(
    { q: trimmedQuery },
    { enabled: isOpen },
  )
  const isEmpty = !isLoading && cities.length === 0

  const rows: CityRowItem[] = useMemo(
    () => cities.map((city) => ({ id: city.id, name: city.name, region: city.region, city })),
    [cities],
  )

  // Подставляем выбранный город только в списке популярных: в выдаче поиска он либо есть
  // сам по себе (и просто отметится галочкой), либо человек ищет другой город.
  const pinned: CityRowItem | null = useMemo(() => {
    if (!selectedCity || isSearching || isLoading) {
      return null
    }
    if (rows.some((row) => row.id === selectedCity.id)) {
      return null
    }

    return {
      id: selectedCity.id,
      name: selectedCity.name,
      region: selectedCity.region ?? null,
      city: null,
    }
  }, [selectedCity, isSearching, isLoading, rows])

  const handleSelect = (item: CityRowItem) => {
    // Единственный момент, когда у клиента есть имя города вместе с id — запоминаем на будущее
    if (item.city) {
      onSelect(item.city)
    }
    onClose()
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} size='TALL'>
      <BottomSheet.Header>
        <VStack gap='SMALL_M'>
          <Text variant='H2' as='h2'>
            {copy.onb.a2.city}
          </Text>
          {/*
            Поиск живёт в шапке, а не в начале Content: скроллится только Content, и поле
            обязано оставаться на месте при прокрутке списка. data-vaul-no-drag — чтобы жест
            по полю не превращался в закрывающий драг шторки (шапка у vaul — зона драга);
            остальная шапка тянется вниз как раньше.
          */}
          <div data-vaul-no-drag>
            <SearchInput
              value={query}
              placeholder={CITY_SEARCH_PLACEHOLDER}
              onChange={setQuery}
              onClear={() => setQuery('')}
            />
          </div>
        </VStack>
      </BottomSheet.Header>
      {/*
        data-vaul-no-drag — это скроллируемый список (весь Content), свайп по нему должен
        прокручивать, а не закрывать шторку. Закрыть свайпом по-прежнему можно за шапку.
      */}
      <BottomSheet.Content>
        <div className={styles.body} data-vaul-no-drag>
          {pinned && <CityRow item={pinned} isSelected={true} onPress={handleSelect} />}

          {!isSearching && !isLoading && rows.length > 0 && (
            <Text variant='OVERLINE' color='MUTED'>
              {CITY_POPULAR_OVERLINE}
            </Text>
          )}

          <div className={styles.rows}>
            {isLoading && <LoadingState />}

            {!isLoading &&
              rows.map((item) => (
                <CityRow
                  key={item.id}
                  item={item}
                  isSelected={item.id === selectedCity?.id}
                  onPress={handleSelect}
                />
              ))}
          </div>

          {/* Без карточки-подложки: `EmptyState` заворачивает содержимое в `Card`, и внутри
              шторки получалась карточка в карточке. В шторке пустое состояние — это сам экран,
              поэтому `Placeholder` напрямую и крупнее (правка основателя). */}
          {isEmpty && isSearching && (
            <div className={styles.empty}>
              <Placeholder
                name='EMPTY'
                size='L'
                title={CITY_EMPTY_TITLE}
                description={CITY_EMPTY_DESCRIPTION}
              />
            </div>
          )}
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  )
})
